import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboardIcon,
  UserIcon,
  Users,
  SquareUserRound,
  Share2,
  LogOutIcon,
  MenuIcon,
  ChevronLeftIcon,
  SunIcon,
  MoonIcon,
  MonitorCog,
  ReceiptText,
  FileBox,
  Boxes,
  Router,
  UserX,
  OctagonX,
  FileText,
  ActivityIcon,
  LayoutDashboard,
} from "lucide-react";

const Sidebar = ({ role, onLogout, isOpen, toggleSidebar }) => {
  const [isDark, setIsDark] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  /* =========================
     DARK MODE
  ========================== */
  useEffect(() => {
    const saved = localStorage.getItem("darkMode") === "true";
    setIsDark(saved);
    document.documentElement.classList.toggle("dark", saved);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("darkMode", next);
    document.documentElement.classList.toggle("dark", next);
  };

  /* =========================
     RESPONSIVE DETECTION
  ========================== */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* =========================
     MENU CONFIG
  ========================== */
  const menuItems = [
    {
      name: "Dashboard",
      icon: <LayoutDashboardIcon size={18} />,
      path: "/dashboard",
      roles: ["admin"],
    },
    {
      name: "Profile",
      icon: <UserIcon size={18} />,
      path: "/profile",
      roles: [
        "admin",
        "member",
        "NOC",
        "NOC-WFH",
        "billing",
        "Customer Service",
        "Marketing",
      ],
    },
    {
      name: "Users",
      icon: <Users size={18} />,
      path: "/users",
      roles: ["admin"],
    },
    {
      name: "Devices",
      icon: <Router size={18} />,
      path: "/devices",
      roles: ["admin", "NOC"],
    },
    {
      name: "Mikrotik Users",
      icon: <MonitorCog size={18} />,
      path: "/mikrotikusers",
      roles: ["admin", "NOC"],
    },
    {
      name: "User Manager",
      icon: <MonitorCog size={18} />,
      path: "/profilemanager",
      roles: ["admin", "NOC"],
    },
    {
      name: "PPP",
      icon: <MonitorCog size={18} />,
      path: "/ppp",
      roles: ["admin", "NOC"],
    },
    {
      name: "WFH PPP",
      icon: <MonitorCog size={18} />,
      path: "/ppp-wfh",
      roles: ["admin", "NOC", "NOC-WFH"],
    },
    {
      name: "Customer Dashboard",
      icon: <LayoutDashboard size={18} />,
      path: "/customer-dashboard",
      roles: [
        "admin",
        "member",
        "NOC",
        "billing",
        "Customer Service",
        "Marketing",
      ],
    },
    {
      name: "Customers",
      icon: <SquareUserRound size={18} />,
      path: "/customers",
      roles: [
        "admin",
        "member",
        "NOC",
        "billing",
        "Customer Service",
        "Marketing",
      ],
    },
    {
      name: "Customers Network",
      icon: <Share2 size={18} />,
      path: "/ppp-secrets-all",
      roles: ["admin", "billing", "NOC"],
    },
    {
      name: "WFH Network",
      icon: <Share2 size={18} />,
      path: "/ppp-secrets-wfh",
      roles: ["admin", "billing", "NOC-WFH"],
    },
    {
      name: "Disabled List",
      icon: <UserX size={18} />,
      path: "/disabled-list",
      roles: [
        "admin",
        "member",
        "NOC",
        "billing",
        "Customer Service",
        "Marketing",
      ],
    },
    {
      name: "WFH Disabled List",
      icon: <UserX size={18} />,
      path: "/disabled-list-wfh",
      roles: [
        "admin",
        "member",
        "NOC-WFH",
        "billing",
        "Customer Service",
        "Marketing",
      ],
    },
    {
      name: "Dismantle List",
      icon: <OctagonX size={18} />,
      path: "/dismantle-list",
      roles: ["admin"],
    },
    {
      name: "Product Group",
      icon: <Boxes size={18} />,
      path: "/productgroups",
      roles: ["admin", "member", "billing", "Customer Service", "Marketing"],
    },
    {
      name: "Products",
      icon: <FileBox size={18} />,
      path: "/products",
      roles: ["admin", "member", "billing", "Customer Service", "Marketing"],
    },
    {
      name: "Invoices",
      icon: <FileText size={18} />,
      path: "/invoices",
      roles: ["admin", "member", "billing", "Customer Service", "Marketing"],
    },
    {
      name: "SKI-Transactions",
      icon: <ReceiptText size={18} />,
      path: "/transactions",
      roles: ["admin", "member", "billing", "Customer Service", "Marketing"],
    },
    {
      name: "Inovasi-Transactions",
      icon: <ReceiptText size={18} />,
      path: "/inovasi-transactions",
      roles: ["admin", "member", "billing", "Customer Service", "Marketing"],
    },
    {
      name: "Recent Activities",
      icon: <ActivityIcon size={18} />,
      path: "/recent-activities",
      roles: [
        "admin",
        "member",
        "billing",
        "Customer Service",
        "Marketing",
        "NOC",
        "NOC-WFH",
      ],
    },
    {
      name: "Logout",
      icon: <LogOutIcon size={18} />,
      roles: [
        "admin",
        "member",
        "NOC",
        "NOC-WFH",
        "billing",
        "Customer Service",
        "Marketing",
      ],
    },
  ];

  /* =========================
     RENDER
  ========================== */
  return (
    <>
      {/* MOBILE OVERLAY */}
      {isMobile && isOpen && (
        <div
          onClick={toggleSidebar}
          className="fixed inset-0 bg-black/50 z-30"
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen
          bg-gray-800 dark:bg-gray-900 text-white
          transition-all duration-300
          ${isMobile ? (isOpen ? "translate-x-0" : "-translate-x-full") : ""}
          ${!isMobile ? (isOpen ? "w-64" : "w-20") : "w-64"}
        `}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <span className={`font-bold text-xl ${isOpen ? "block" : "hidden"}`}>
            BilNet
          </span>

          {!isMobile && (
            <button onClick={toggleSidebar}>
              {isOpen ? <ChevronLeftIcon size={20} /> : <MenuIcon size={20} />}
            </button>
          )}
        </div>

        {/* MENU (SCROLLABLE) */}
        <nav className="overflow-y-auto h-[calc(100vh-160px)] mt-2">
          {menuItems
            .filter((item) => item.roles.includes(role))
            .map((item, idx) =>
              item.name === "Logout" ? (
                <button
                  key={idx}
                  onClick={onLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-700"
                >
                  {item.icon}
                  <span className={`${isOpen ? "inline" : "hidden"}`}>
                    Logout
                  </span>
                </button>
              ) : (
                <Link
                  key={idx}
                  to={item.path}
                  onClick={() => isMobile && toggleSidebar()}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-700 ${
                    location.pathname === item.path ? "bg-gray-700" : ""
                  }`}
                >
                  {item.icon}
                  <span className={`${isOpen ? "inline" : "hidden"}`}>
                    {item.name}
                  </span>
                </Link>
              )
            )}
        </nav>

        {/* FOOTER */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={toggleDarkMode}
            className="flex items-center gap-3 w-full px-3 py-2 bg-gray-700 rounded hover:bg-gray-600"
          >
            {isDark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
            <span className={`${isOpen ? "inline" : "hidden"}`}>
              {isDark ? "Light Mode" : "Dark Mode"}
            </span>
          </button>

          <p className={`mt-3 text-xs ${isOpen ? "block" : "hidden"}`}>
            PT. Sumber Koneksi Indonesia
          </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
